import React from 'react'

import {
  ScheduleIcon,
  HomeIcon,
  DeliveryIcon,
  LocationIcon,
  PickUpIcon
} from '@toasttab/buffet-pui-icons'

import {
  DiningOptionBehavior,
  OrderStatusData
} from 'src/apollo/onlineOrdering'
import {
  removeContactlessIndicator,
  containsContactlessIndicator
} from 'src/public/components/default_template/online_ordering/checkout/checkoutUtils'
import {
  UnifiedOrderStatus,
  deliveryStatusToFulfillmentTime,
  takeoutStatusToFulfillmentTime,
  deliveryStatusToFulfillmentDeadline
} from 'src/public/components/default_template/online_ordering/order_tracker/order_status_bar/statusUtil'
import { CompletedOrder } from 'src/public/components/online_ordering/types'
import Link from 'src/shared/components/common/link'
import { t } from 'src/translations'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'

type TrackerFulfillmentInfoProps = {
  order: CompletedOrder
  orderStatus: UnifiedOrderStatus
  orderStatusData?: OrderStatusData
  isUnderHourAfterDelivery: boolean
}

export const TrackerFulfillmentInfo = ({
  order,
  orderStatus,
  orderStatusData,
  isUnderHourAfterDelivery
}: TrackerFulfillmentInfoProps) => {
  const tracker = useTracker()
  const destination = encodeURIComponent(
    [
      order.restaurant.location.address1,
      order.restaurant.location.address2 || '',
      order.restaurant.location.city,
      order.restaurant.location.state,
      order.restaurant.location.zip
    ].join(' ')
  )
  const directionsLink = `https://www.google.com/maps/dir/?api=1&destination=${destination}`
  const { ooRestaurant } = useRestaurant()
  const locale = ooRestaurant?.i18n.locale
  const timeZoneId = ooRestaurant?.timeZoneId ?? undefined
  const fulfillmentDateInfo =
    order.diningOptionBehavior === DiningOptionBehavior.TakeOut
      ? takeoutStatusToFulfillmentTime(orderStatus, order)
      : deliveryStatusToFulfillmentTime(
          orderStatus,
          orderStatusData,
          order,
          locale,
          timeZoneId
        )

  const fulfillmentDeadlineInfo =
    order.diningOptionBehavior === DiningOptionBehavior.Delivery
      ? deliveryStatusToFulfillmentDeadline(orderStatusData, locale, timeZoneId)
      : null

  return (
    <div>
      {order.diningOptionBehavior === DiningOptionBehavior.Delivery ? (
        <div>
          {fulfillmentDateInfo && (
            <div className='fulfillmentRow'>
              <div className='icon'>
                <ScheduleIcon />
              </div>
              <div className='header estimatedArrival'>
                {fulfillmentDateInfo}
                {fulfillmentDeadlineInfo && (
                  <div className='dropoffDeadline'>
                    {fulfillmentDeadlineInfo}
                  </div>
                )}
              </div>
            </div>
          )}
          {isUnderHourAfterDelivery && (
            <div className='fulfillmentRow'>
              <div className='icon'>
                <HomeIcon />
              </div>
              <div>
                <div className='header'>
                  {t('tracker-fulfillment-info.delivery-location')}
                </div>
                {order.deliveryInfo && (
                  <div className='deliveryAddress'>
                    <div>{order.deliveryInfo.address1}</div>
                    <div>{order.deliveryInfo.address2}</div>
                    <div>
                      {order.deliveryInfo?.city}, {order.deliveryInfo.state}{' '}
                      {order.deliveryInfo.zipCode}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {order.deliveryInfo?.notes && (
            <div data-testid='delivery-notes' className='fulfillmentRow'>
              <div className='icon'>
                <DeliveryIcon />
              </div>
              <div>
                <div className='header'>
                  {t('tracker-fulfillment-info.delivery')}
                </div>
                <div className='deliveryInstructions'>
                  {t('tracker-fulfillment-info.instructions', {
                    instructions: removeContactlessIndicator(
                      order.deliveryInfo?.notes
                    )
                  })}
                </div>
                {containsContactlessIndicator(order.deliveryInfo?.notes) && (
                  <div className='contactlessNote'>
                    {t('tracker-fulfillment-info.leave-at-door')}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          {fulfillmentDateInfo && (
            <div className='fulfillmentRow'>
              <div className='icon'>
                <ScheduleIcon />
              </div>
              <div className='header'>{fulfillmentDateInfo}</div>
            </div>
          )}
          <div className='fulfillmentRow'>
            <div className='icon'>
              <LocationIcon />
            </div>
            <div>
              <div className='header'>{order.restaurant.location.address1}</div>
              <Link
                className='directions'
                href={directionsLink}
                onClick={() =>
                  tracker.track(TrackingEvents.GOOGLE_MAPS_LINK_CLICKED, {
                    context: 'order_tracker_link',
                    restaurant_guid: order.restaurant.guid
                  })
                }
              >
                {t('tracker-fulfillment-info.get-directions')}
              </Link>
            </div>
          </div>
          <div className='fulfillmentRow'>
            <div className='icon'>
              <PickUpIcon />
            </div>
            <div>
              <div className='header'>
                {t('tracker-fulfillment-info.pickup')}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
